import { Injectable } from '@angular/core';

export interface lmapAction {
  name: string;
  task: string;
  //eslint-disable-next-line
  option: any;
  //eslint-disable-next-line
  destination: any;
  tag: Array<string>;
}

@Injectable()
export class LmapService {
  supportedModelList = {
    modellist: [
      'NSC',
      'TB/MTS-5800-100G',
      'TB/MTS-5800v2',
      'MTS-5882',
      'SC4800',
      'SC4800P',
      'TB-5800v2',
      'MTS-5800v2',
      'TB-5882',
      'MAP-2100',
      'TB-5800-100G',
      'MTS-5800-100G',
      'vTA',
      'vTA-TS',
      'vPMA',
      'vPMA-L',
      'vQT',
      'QT600-10',
      'JMEP-1G'
    ]
  };
  CONST = {
    resultTypeOptionId: 'result-type',
    resultSequenceId: 'seq',
    resultRunId: 'runId',
    resultPostDateId: 'postDate', //value of 'ietf-lmap-report:report/date' which is when probe sent data to collector
    resultStartDateId: 'resultStart', //start of measurement
    resultEndDateId: 'resultEnd', //end of measurement (optional)
    resultFormatType: 'v1'
  };
  //eslint-disable-next-line
  constructor() {}

  // eslint-disable-next-line
  createAction(
    ACTION_NAME: string,
    TASK_NAME: string,
    //eslint-disable-next-line
    optionArray: any,
    //eslint-disable-next-line
    destinationArray?: any,
    tags?: Array<string>
    //eslint-disable-next-line
  ): any {
    // destinationArray is optional
    if (
      typeof ACTION_NAME !== 'string' ||
      typeof TASK_NAME !== 'string' ||
      typeof optionArray !== 'object'
    ) {
      return null;
    }

    const lmapAction: lmapAction = {
      name: ACTION_NAME,
      task: TASK_NAME,
      option: null,
      destination: null,
      tag: null
    };

    if (optionArray) {
      lmapAction.option = optionArray;
    }

    if (typeof destinationArray === 'object') {
      lmapAction.destination = destinationArray;
    }
    if (tags) {
      lmapAction.tag = tags;
    }

    return lmapAction;
  }

  // createSchedule
  // params:
  //   scheduleName = name of schedule
  //   eventName = name of event schedule references
  //   actionArray = array of objects returned from createAction()
  // output:
  //   LMAP-formatted schedule object that should be passed to addSchedule()
  /*  Schedule structure
    {
        "name": "SCHED_NAME",
        "event": "EVENT_NAME",
        "action": [ARRAY_OF_LMAP_ACTIONS]
    }
*/
  // eslint-disable-next-line
  createSchedule(SCHED_NAME: string, EVENT_NAME: string, actionArray: any): any {
    if (
      typeof SCHED_NAME !== 'string' ||
      typeof EVENT_NAME !== 'string' ||
      typeof actionArray !== 'object'
    ) {
      return null;
    }

    const lmapSchedule = {
      name: SCHED_NAME,
      start: EVENT_NAME,
      action: actionArray
    };

    return lmapSchedule;
  }

  // createPeriodicEvent
  // params:
  //   eventName = name of event
  //   interval = integer value
  //   start = yang:date-and-time (optional)
  //   end = yang:date-and-time (optional)
  // output:
  //   LMAP-formatted event object that should be passed to addEvent()
  /*  Periodic Event structure
    {
        "name": "EVENT_NAME",
        "periodic": {
            "interval": "EVENT_INTERVAL"
            "start":    "START_TIME"
            "end":      "END_TIME"
        }
    }
*/
  createPeriodicEvent(
    EVENT_NAME: string,
    EVENT_INTERVAL: number,
    START_TIME: string,
    END_TIME: string
    //eslint-disable-next-line
  ): any {
    if (typeof EVENT_NAME !== 'string' || typeof EVENT_INTERVAL !== 'number') {
      return null;
    }

    const lmapPeriodicEvent = {
      name: EVENT_NAME,
      periodic: null
    };

    const periodic = {
      interval: EVENT_INTERVAL,
      start: null,
      end: null
    };

    if (typeof START_TIME === 'string') {
      periodic.start = START_TIME;
    }
    if (typeof END_TIME === 'string') {
      periodic.end = END_TIME;
    }
    lmapPeriodicEvent.periodic = periodic;

    return lmapPeriodicEvent;
  }

  // addSchedule
  // inserts a schedule into a new or existing LMAP structure
  // params:
  //    newSchedule = output from createSchedule()
  //    existingConfig = null or a prior return value from this function
  // output:
  //    LMAP-formatted object that contains the new schedule
  // Expected use case:
  // Invoke first time with null for existingConfig + test schedule data
  // For each report schedule, invoke again with the prior response + report schedule data
  //eslint-disable-next-line
  addSchedule(newSchedule: any, existingConfig?: any): any {
    if (typeof newSchedule !== 'object') {
      return null;
    }

    let lmapConfig = existingConfig;

    if (typeof existingConfig !== 'object' || this.isEmptyObject(existingConfig)) {
      const scheduleArray = [];
      scheduleArray.push(newSchedule);
      const schedule = {
        schedule: scheduleArray
      };
      const schedules = {
        schedules: schedule
      };
      lmapConfig = {};
      lmapConfig['ietf-lmap-control:lmap'] = schedules;
    } else {
      if (
        lmapConfig['ietf-lmap-control:lmap'].schedules &&
        lmapConfig['ietf-lmap-control:lmap'].schedules.schedule &&
        typeof lmapConfig['ietf-lmap-control:lmap'].schedules.schedule === 'object'
      ) {
        lmapConfig['ietf-lmap-control:lmap'].schedules.schedule.push(newSchedule);
      }
    }
    return lmapConfig;
  }

  // addEvent
  // inserts an event into an existing LMAP structure
  // params:
  //    existingConfig = prior return value from this function or addSchedule()
  //    newEvent = output from createPeriodicEvent()
  // output:
  //    LMAP-formatted object that contains the new event
  //eslint-disable-next-line
  addEvent(existingConfig: any, newEvent: any): any {
    if (
      typeof newEvent !== 'object' ||
      typeof existingConfig !== 'object' ||
      typeof existingConfig['ietf-lmap-control:lmap'] === 'undefined'
    ) {
      return null;
    }

    if (
      existingConfig['ietf-lmap-control:lmap'].events &&
      existingConfig['ietf-lmap-control:lmap'].events.event
    ) {
      existingConfig['ietf-lmap-control:lmap'].events.event.push(newEvent);
    } else {
      const eventArray = [];
      eventArray.push(newEvent);
      const eventObj = {
        event: eventArray
      };
      existingConfig['ietf-lmap-control:lmap'].events = eventObj;
    }
    return existingConfig;
  }

  //eslint-disable-next-line
  isEmptyObject(config: any): boolean {
    return config && Object.keys(config).length === 0;
  }

  // addResultScheduleAndEvent
  // creates a new result schedule and inserts it into an existing LMAP structure
  // creates a new event (if necessary) and inserts it into an existing LMAP structure
  // ensures the test schedule contains a reference to the new result schedule
  // params:
  //    existingConfig = prior return value from this function or addSchedule()
  //    testScheduleName = name of the test schedule that must exist in existingConfig
  //    eventType = immediate or periodic
  //    resultType = rt, pm or final
  //    resultUrl = url MA will use to send results to collector
  //    interval = for periodic event types, the interval in milliseconds
  // output:
  //    LMAP-formatted object that contains the updated content

  //eslint-disable-next-line
  addResultScheduleAndEvent(
    //eslint-disable-next-line
    existingConfig: any,
    testScheduleName: string,
    testEventName: string,
    eventType: string,
    resultType: string,
    resultUrl: string,
    interval?: number,
    //eslint-disable-next-line
    startTime?: any,
    //eslint-disable-next-line
    endTime?: any
    //eslint-disable-next-line
  ): any {
    if (
      typeof existingConfig !== 'object' ||
      this.isEmptyObject(existingConfig) ||
      typeof testScheduleName !== 'string' ||
      typeof testEventName !== 'string' ||
      typeof eventType !== 'string' ||
      typeof resultType !== 'string' ||
      typeof resultUrl !== 'string'
    ) {
      return existingConfig;
    }

    if (
      typeof existingConfig['ietf-lmap-control:lmap'] === 'undefined' ||
      typeof existingConfig['ietf-lmap-control:lmap'].schedules === 'undefined' ||
      typeof existingConfig['ietf-lmap-control:lmap'].schedules.schedule === 'undefined'
    ) {
      return existingConfig;
    }

    const taskName = 'results'; // for now, all our MAs will use the same result task name
    const resultSchedName = testScheduleName + '-' + taskName + '-' + eventType + '-' + resultType;

    // Before we do anything else, locate the test schedule so we can add a reference to this new result schedule
    let updatedTestSchedule = false;
    for (
      let i = 0, len = existingConfig['ietf-lmap-control:lmap'].schedules.schedule.length;
      i < len;
      ++i
    ) {
      if (
        existingConfig['ietf-lmap-control:lmap'].schedules.schedule[i].name === testScheduleName
      ) {
        if (
          existingConfig['ietf-lmap-control:lmap'].schedules.schedule[i].action &&
          existingConfig['ietf-lmap-control:lmap'].schedules.schedule[i].action.length >= 1
        ) {
          if (
            existingConfig['ietf-lmap-control:lmap'].schedules.schedule[i].action[0].destination
          ) {
            let resultSchedAlreadyExists = false;
            for (
              let x = 0,
                lenDest =
                  existingConfig['ietf-lmap-control:lmap'].schedules.schedule[i].action[0]
                    .destination.length;
              x < lenDest;
              ++x
            ) {
              if (
                existingConfig['ietf-lmap-control:lmap'].schedules.schedule[i].action[0]
                  .destination[x] === resultSchedName
              ) {
                resultSchedAlreadyExists = true;
                break;
              }
            }
            if (resultSchedAlreadyExists == false) {
              existingConfig['ietf-lmap-control:lmap'].schedules.schedule[
                i
              ].action[0].destination.push(resultSchedName);
              updatedTestSchedule = true;
            } // else result schedule already exists so return from this function without adding anything
          } else {
            existingConfig['ietf-lmap-control:lmap'].schedules.schedule[i].action[0].destination = [
              resultSchedName
            ];
            updatedTestSchedule = true;
          }
        }
        break;
      }
    }
    if (updatedTestSchedule == false) {
      return existingConfig; // either test schedule does not exist or result schedule already exists...either way, nothing to do
    }

    let eventName = 'immediate';
    if (eventType != 'immediate') {
      // Note: event names have to be unique; thus we reuse the test schedule name
      eventName = resultSchedName + '-event';
    }

    const actionName = resultSchedName + '-action';

    const actionOption = {
      id: this.CONST.resultTypeOptionId,
      value: resultType
    };
    const actionOption_2 = {
      id: 'channel',
      value: resultUrl
    };

    const actionOption_3 = {
      id: 'result-format',
      value: 'v2'
    };

    const optionArray = [];
    optionArray.push(actionOption);
    optionArray.push(actionOption_2);
    if (this.CONST.resultFormatType === 'v2') {
      optionArray.push(actionOption_3);
    }

    const action = this.createAction(actionName, taskName, optionArray);
    const actionArray = [];
    actionArray.push(action);

    const schedule = this.createSchedule(resultSchedName, eventName, actionArray);
    existingConfig = this.addSchedule(schedule, existingConfig);

    // immediate schedules do not need an event since MA is assumed to provide one that is named 'immediate'
    if (eventType == 'periodic' && typeof interval === 'number') {
      const event1 = this.createPeriodicEvent(testEventName, interval, startTime, endTime);
      existingConfig = this.addEvent(existingConfig, event1);
      const event2 = this.createPeriodicEvent(eventName, 1, null, null);
      existingConfig = this.addEvent(existingConfig, event2);
    }

    return existingConfig;
  }

  //
  // buildCollectorPostUrl
  // combines collector data into ULR for use by MA to send results
  //eslint-disable-next-line
  buildCollectorPostUrl(serverConfig: any, deviceModel: any): any {
    let collectorUrl = null;

    if (
      serverConfig &&
      serverConfig['fusion-collector'] &&
      serverConfig['fusion-collector'].protocol &&
      serverConfig['fusion-collector'].hostname &&
      serverConfig['fusion-collector'].port &&
      serverConfig['fusion-collector'].reportUrl
    ) {
      let myhostname = serverConfig['fusion-collector']['hostname'];

      if (myhostname === '0.0.0.0' || myhostname === '127.0.0.1' || myhostname === 'localhost') {
        if (serverConfig['fusion-collector']['IPv4']) {
          myhostname = serverConfig['fusion-collector']['IPv4'];
        } else if (serverConfig['fusion-collector']['IPv6']) {
          myhostname = serverConfig['fusion-collector']['IPv6'];
        }
      }

      if (deviceModel && this.supportedModelList.modellist.indexOf(deviceModel) >= 0) {
        myhostname = '0.0.0.0';
      }

      collectorUrl =
        serverConfig['fusion-collector'].protocol +
        '://' +
        myhostname +
        ':' +
        serverConfig['fusion-collector'].port;
      if (serverConfig['fusion-collector'].reportUrl.indexOf('/') != 0) {
        collectorUrl += '/';
      }
      collectorUrl += serverConfig['fusion-collector'].reportUrl;
    }
    return collectorUrl;
  }

  //------- Report related functionality ------------------

  //
  // translateLmapReportIndexToAngular
  // Given an array of reports returned from the Collector, one is translated
  //
  // inputs:
  // lmapObj = the response from our collector which is an array of reports
  // reportIndex = the report index in the array to parse (if not provided the last array entry will be used)
  // tableType = the type of table (value in tag) to translate [optional]
  //eslint-disable-next-line
  ltranslateLmapReportIndexToAngular(lmapObj: any, tableType?: any, reportIndex?: number): any {
    let newReport = [];
    let reportIdx = -1;

    if (typeof reportIndex === 'number') {
      reportIdx = reportIndex;
    } else if (lmapObj && typeof lmapObj === 'object') {
      reportIdx = lmapObj.length - 1;
    }

    if (reportIdx < 0 || typeof lmapObj[reportIdx] === 'undefined') {
      return null;
    }

    newReport = this.translateLmapReportToAngular(lmapObj[reportIdx], tableType);

    if (newReport && newReport instanceof Array && newReport.length > 0) {
      return newReport;
    } else {
      return null;
    }
  }

  // translateLmapReportToAngular
  // converts a JSON LMAP report object into a JSON object of non-primatives for Angular ng-repeat consumption
  // LMAP reports the headers in one array and the data in additional arrays
  // Angular's ng-repeat can't handle arrays of primatives (strings, integers, etc)
  // so an array of objects (column name:row value) are made and returned
  // inputs:
  // lmapObj = a single report instance from the collector
  // tableType = the type of table (value in tag) to translate [optional]

  //eslint-disable-next-line
  translateLmapReportToAngular(lmapObj: any, tableType?: any): any {
    const newReport = [];

    if (lmapObj['ietf-lmap-report:report'] && lmapObj['ietf-lmap-report:report'].result) {
      for (const x in lmapObj['ietf-lmap-report:report'].result) {
        // is this the requested table?
        let isRequestedTable = false;

        if (tableType) {
          for (const idx in lmapObj['ietf-lmap-report:report'].result[x].tag) {
            if (
              lmapObj['ietf-lmap-report:report'].result[x].tag[idx] ==
              'table-type:' + tableType
            ) {
              isRequestedTable = true;
            }
          }
        } else {
          isRequestedTable = true;
        }
        if (isRequestedTable == true) {
          let sequence = '';
          let runid = '';
          let resultStart = '';
          let resultEnd = '';

          if (lmapObj['ietf-lmap-report:report'].result[x].start) {
            resultStart = lmapObj['ietf-lmap-report:report'].result[x].start;
          }

          if (lmapObj['ietf-lmap-report:report'].result[x].end) {
            resultEnd = lmapObj['ietf-lmap-report:report'].result[x].end;
          }

          for (const idx in lmapObj['ietf-lmap-report:report'].result[x].tag) {
            if (lmapObj['ietf-lmap-report:report'].result[x].tag[idx].indexOf('sequence:') === 0) {
              sequence = lmapObj['ietf-lmap-report:report'].result[x].tag[idx].split(':')[1];
            }
            if (lmapObj['ietf-lmap-report:report'].result[x].tag[idx].indexOf('run-id:') === 0) {
              runid = lmapObj['ietf-lmap-report:report'].result[x].tag[idx].split(':')[1];
            }
          }

          for (const y in lmapObj['ietf-lmap-report:report'].result[x].table) {
            for (const z in lmapObj['ietf-lmap-report:report'].result[x].table[y].row) {
              const newRowData = {};
              let rowCreated = false; //using flag since js does not offer access to length property of object
              if (
                lmapObj['ietf-lmap-report:report'].result[x].table[y].column.length ==
                lmapObj['ietf-lmap-report:report'].result[x].table[y].row[z].value.length
              ) {
                rowCreated = true;
                for (const w in lmapObj['ietf-lmap-report:report'].result[x].table[y].column) {
                  const data =
                    lmapObj['ietf-lmap-report:report'].result[x].table[y].row[z].value[w];
                  newRowData[lmapObj['ietf-lmap-report:report'].result[x].table[y].column[w]] =
                    data;
                }
              }
              if (rowCreated == true) {
                newRowData[this.CONST.resultSequenceId] = sequence;
                newRowData[this.CONST.resultRunId] = runid;
                if (lmapObj['ietf-lmap-report:report'].date) {
                  newRowData[this.CONST.resultPostDateId] = lmapObj['ietf-lmap-report:report'].date;
                }
                if (resultStart != '') {
                  newRowData[this.CONST.resultStartDateId] = resultStart;
                }
                if (resultEnd != '') {
                  newRowData[this.CONST.resultEndDateId] = resultEnd;
                }
                newReport.push(newRowData);
              }
            }
          }
        }
      }
    }
    if (newReport && newReport instanceof Array && newReport.length > 0) {
      return newReport;
    } else {
      return null;
    }
  }

  // --------- New Stuff --------------

  // Given a FULL LMAP object, return the Report Schedule object based on the result type (pm, rt or final)
  //
  //eslint-disable-next-line
  extractReportScheduleByType(lmapObj: any, resultType: string): any {
    if (
      lmapObj['ietf-lmap-control:lmap'] &&
      lmapObj['ietf-lmap-control:lmap'].schedules &&
      lmapObj['ietf-lmap-control:lmap'].schedules.schedule
    ) {
      for (const x in lmapObj['ietf-lmap-control:lmap'].schedules.schedule) {
        if (lmapObj['ietf-lmap-control:lmap'].schedules.schedule[x].action) {
          for (const y in lmapObj['ietf-lmap-control:lmap'].schedules.schedule[x].action) {
            if (lmapObj['ietf-lmap-control:lmap'].schedules.schedule[x].action[y].option) {
              for (const z in lmapObj['ietf-lmap-control:lmap'].schedules.schedule[x].action[y]
                .option) {
                if (
                  lmapObj['ietf-lmap-control:lmap'].schedules.schedule[x].action[y].option[z].id ===
                  this.CONST.resultTypeOptionId
                ) {
                  if (
                    lmapObj['ietf-lmap-control:lmap'].schedules.schedule[x].action[y].option[z]
                      .value === resultType
                  ) {
                    return lmapObj['ietf-lmap-control:lmap'].schedules.schedule[x];
                  }
                }
              }
            }
          }
        }
      }
    }
    return null;
  }

  // Given a FULL LMAP object, return the Event object based on event name
  //
  //eslint-disable-next-line
  extractEventByName(lmapObj: any, eventName: string): any {
    if (
      lmapObj &&
      lmapObj['ietf-lmap-control:lmap'] &&
      lmapObj['ietf-lmap-control:lmap'].events &&
      lmapObj['ietf-lmap-control:lmap'].events.event
    ) {
      for (const x in lmapObj['ietf-lmap-control:lmap'].events.event) {
        if (lmapObj['ietf-lmap-control:lmap'].events.event[x].name === eventName) {
          return lmapObj['ietf-lmap-control:lmap'].events.event[x];
        }
      }
    }
    return null;
  }

  // Given a Schedule LMAP object, return the option object based on option name
  //
  //eslint-disable-next-line
  extractScheduleOptionByName(schedObj: any, optionId: number | string): any {
    if (schedObj && schedObj.action) {
      for (const x in schedObj.action) {
        if (schedObj.action[x].option) {
          for (const y in schedObj.action[x].option) {
            if (schedObj.action[x].option[y].id === optionId) {
              return schedObj.action[x].option[y].value;
            }
          }
        }
      }
    }
    return null;
  }

  // Given a FULL LMAP object, return the Schedule object based on a task name
  //
  //eslint-disable-next-line
  extractScheduleByTaskName(lmapObj: any, taskName: string): any {
    if (
      lmapObj['ietf-lmap-control:lmap'] &&
      lmapObj['ietf-lmap-control:lmap'].schedules &&
      lmapObj['ietf-lmap-control:lmap'].schedules.schedule
    ) {
      for (const x in lmapObj['ietf-lmap-control:lmap'].schedules.schedule) {
        if (lmapObj['ietf-lmap-control:lmap'].schedules.schedule[x].action) {
          for (const y in lmapObj['ietf-lmap-control:lmap'].schedules.schedule[x].action) {
            if (
              lmapObj['ietf-lmap-control:lmap'].schedules.schedule[x].action[y].task === taskName
            ) {
              return lmapObj['ietf-lmap-control:lmap'].schedules.schedule[x];
            }
          }
        }
      }
    }
    return null;
  }

  // return either the primary agent data or the list of secondary agents from the controller configuration
  //eslint-disable-next-line
  getAgentInfo(agentType: string, controllerConfig: any): any {
    if (agentType && controllerConfig && controllerConfig.agent) {
      if (agentType === 'primary') {
        if (controllerConfig.agent.primary) {
          return controllerConfig.agent.primary;
        }
      } else if (agentType === 'secondary') {
        if (controllerConfig.agent.secondary) {
          return controllerConfig.agent.secondary;
        }
      }
    }
    return null;
  }

  //eslint-disable-next-line
  getMATestConfigByName(controllerConfig: any, configName: string): any {
    if (
      configName &&
      controllerConfig.config &&
      controllerConfig.config.length &&
      controllerConfig.config.length > 0
    ) {
      for (const x in controllerConfig.config) {
        for (const y in controllerConfig.config[x]) {
          if (y === configName) {
            return controllerConfig.config[x][y];
          }
        }
      }
    }
    return null;
  }

  // A typical LMAP test request will contain schedules for the test task and for one or more result tasks
  // This function attempts to identify the test schedule and return it
  // if there is only one schedule it can be assumed to represent the test task
  // if there are multiple schedules, the one that contains the destination object can be assumed to represent the test task
  //eslint-disable-next-line
  getTestSchedule(lmapConfig: any): any {
    if (
      lmapConfig &&
      lmapConfig['ietf-lmap-control:lmap'] &&
      lmapConfig['ietf-lmap-control:lmap'].schedules &&
      lmapConfig['ietf-lmap-control:lmap'].schedules.schedule &&
      lmapConfig['ietf-lmap-control:lmap'].schedules.schedule instanceof Array
    ) {
      const numSched = lmapConfig['ietf-lmap-control:lmap'].schedules.schedule.length;
      let testSched = null;
      if (numSched == 1) {
        testSched = lmapConfig['ietf-lmap-control:lmap'].schedules.schedule[0];
      } else {
        for (const x in lmapConfig['ietf-lmap-control:lmap'].schedules.schedule) {
          if (
            lmapConfig['ietf-lmap-control:lmap'].schedules.schedule[x].action &&
            lmapConfig['ietf-lmap-control:lmap'].schedules.schedule[x].action instanceof Array &&
            lmapConfig['ietf-lmap-control:lmap'].schedules.schedule[x].action.length > 0 &&
            lmapConfig['ietf-lmap-control:lmap'].schedules.schedule[x].action[0].destination
          ) {
            testSched = lmapConfig['ietf-lmap-control:lmap'].schedules.schedule[x];
            return testSched;
          }
        }
      }
    }
    return null;
  }

  //
  // get test options from an lmap structure
  //
  // taskName - used to find the schedule
  // optArray - a list of 'strings' of options to look for
  // config - the lmap JSON object
  //
  // Returns a JSON object of name/value pairs where the name is the optArray name given and the value is what it finds.
  // JSON strings are not converted. It's up to the caller to do that. It only returns optArray values that were found,
  // otherwise the JSON object is empty.
  //
  //eslint-disable-next-line
  getTestOptions(taskName: string, optArray: any, config: any): any {
    const returnObj = {};
    const primaryAgentInfo = this.getAgentInfo('primary', config);
    if (primaryAgentInfo) {
      const primaryConfig = this.getMATestConfigByName(config, primaryAgentInfo.config);
      if (primaryConfig) {
        const testSchedule = this.extractScheduleByTaskName(primaryConfig, taskName);
        if (testSchedule) {
          for (let x = 0, xlen = optArray.length; x < xlen; ++x) {
            const testOption = this.extractScheduleOptionByName(testSchedule, optArray[x]);
            if (testOption) {
              returnObj[optArray[x]] = testOption;
            }
          }
        }
      }
    }
    return returnObj;
  }

  //
  // Read date/time measurement period ended - per LMAP this is an optional field in the report
  // dataSet - response from Collector
  // reportIdx - index of report to extract data
  //
  //eslint-disable-next-line
  getReportEndDate(dataSet: any, reportIdx: number): string {
    if (dataSet && dataSet.data) {
      // handle arrays of reports
      if (dataSet.data.length) {
        if (dataSet.data[reportIdx] && dataSet.data[reportIdx]['ietf-lmap-report:report']) {
          if (
            dataSet.data[reportIdx]['ietf-lmap-report:report'].result &&
            dataSet.data[reportIdx]['ietf-lmap-report:report'].result[0] &&
            dataSet.data[reportIdx]['ietf-lmap-report:report'].result[0].end
          ) {
            return dataSet.data[reportIdx]['ietf-lmap-report:report'].result[0].end;
          }
        }
      }
      // handle single reports
      else if (dataSet.data['ietf-lmap-report:report']) {
        if (
          dataSet.data['ietf-lmap-report:report'].result &&
          dataSet.data['ietf-lmap-report:report'].result[0] &&
          dataSet.data['ietf-lmap-report:report'].result[0].end
        ) {
          return dataSet.data['ietf-lmap-report:report'].result[0].end;
        }
      }
    }

    return '';
  }

  //
  // Read date/time probe posted report to collector from RAW lmap report
  // dataSet - response from Collector
  // reportIdx - index of report to extract data
  //
  //eslint-disable-next-line
  getReportPostDate(dataSet: any, reportIdx: number): string {
    if (dataSet && dataSet.data) {
      // handle arrays of reports
      if (dataSet.data.length) {
        if (dataSet.data[reportIdx] && dataSet.data[reportIdx]['ietf-lmap-report:report']) {
          if (dataSet.data[reportIdx]['ietf-lmap-report:report'].date) {
            return dataSet.data[reportIdx]['ietf-lmap-report:report'].date;
          }
        }
      }
      // handle single reports
      else if (dataSet.data['ietf-lmap-report:report']) {
        if (dataSet.data['ietf-lmap-report:report'].date) {
          return dataSet.data['ietf-lmap-report:report'].date;
        }
      }
    }

    return '';
  }

  //
  // Read tag from RAW lmap report
  // dataSet - response from Collector
  // reportIdx - index of report to extract data
  //
  //eslint-disable-next-line
  getReportTag(dataSet: any, reportIdx: number): string {
    if (dataSet && dataSet.data) {
      // handle arrays of reports
      if (dataSet.data.length) {
        if (dataSet.data[reportIdx] && dataSet.data[reportIdx]['ietf-lmap-report:report']) {
          if (
            dataSet.data[reportIdx]['ietf-lmap-report:report']['result'] &&
            dataSet.data[reportIdx]['ietf-lmap-report:report']['result'][0] &&
            dataSet.data[reportIdx]['ietf-lmap-report:report']['result'][0].tag
          ) {
            return dataSet.data[reportIdx]['ietf-lmap-report:report']['result'][0].tag;
          }
        }
      }
      // handle single reports
      else if (dataSet.data['ietf-lmap-report:report']) {
        if (
          dataSet.data['ietf-lmap-report:report']['result'] &&
          dataSet.data['ietf-lmap-report:report']['result'][0] &&
          dataSet.data['ietf-lmap-report:report']['result'][0].tag
        ) {
          return dataSet.data['ietf-lmap-report:report']['result'][0].tag;
        }
      }
    }
    return '';
  }

  //
  // extract a value for a specific tag
  // tags - lmap tag object which is an array of strings
  // targetTagName - 'name' of the string whose value you want (each of our tags are in the format '<name>:<value>')
  //eslint-disable-next-line
  getTagValue(tags: any, targetTagName: any): string {
    if (tags && targetTagName) {
      for (const idx in tags) {
        if (tags[idx].indexOf(targetTagName + ':') === 0) {
          const colonIndex = tags[idx].indexOf(':');
          return tags[idx].substring(colonIndex + 1);
        }
      }
    }
    return null;
  }
}
